import { theme } from '@/styles';
import { SearchAddress } from '@remodeling/common';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import Divider from '../../components/Divider/Divider';
import Flag from '../Flag';
import EmptyIcon from '../Icons/EmptyIcon';

const MINIMUM_EXCEEDING_VALUE = 30;

interface SearchAddressResultType {
  page: number;
  value: string;
  totalNum: number;
  addressList: SearchAddress[];
  handleSearchAddress: (address: SearchAddress) => void;
}

function SearchAddressResult({
  page,
  value,
  totalNum,
  addressList,
  handleSearchAddress,
}: SearchAddressResultType) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 페이지 또는 인풋값이 변경될 경우 스크롤 위치 최상단으로 이동
    if (!ref.current) return;
    ref.current.scrollTo(0, 0);
  }, [page, value]);

  return (
    <>
      {totalNum !== 0 && totalNum > MINIMUM_EXCEEDING_VALUE && (
        <div>
          <div>
            <DetailInfo>
              <Detail>
                <span>{`“`}</span>
                <ActiveText>{`${value}`}</ActiveText>
                <span>{`”`}</span>
                <span>{` 검색한 결과 총 `}</span>
                <ActiveText>{`${totalNum}`}</ActiveText>
                <span>{`건 입니다.`}</span>
              </Detail>
              <SubDetail>
                <div>다음과 같이 검색하시면 정확한 결과가 검색됩니다.</div>
                <div>
                  ‘도로명+건물번호’, ‘지역명+번지’, ‘지역명+건물명(아파트명)
                </div>
              </SubDetail>
            </DetailInfo>
            <Divider h={'1px'} mt={'16px'} />
          </div>
          <AddressList totalNum={totalNum} ref={ref}>
            {addressList.map((address, idx) => {
              return (
                <React.Fragment key={`address-${address.addrRoad}-${idx}`}>
                  <AddressListItemWrap
                    onClick={() => handleSearchAddress(address)}
                  >
                    <AddressListItem>
                      <AddressWrap>
                        <AddressBox>
                          <Flag flagColor="mono" width="44px">
                            도로명
                          </Flag>
                          <Address>{address.addrRoad}</Address>
                        </AddressBox>
                        <AddressBox>
                          <Flag flagColor="mono" width="44px">
                            지번
                          </Flag>
                          <Address>{address.addrJibun}</Address>
                        </AddressBox>
                      </AddressWrap>
                    </AddressListItem>
                    <PostNum>{address.post5No}</PostNum>
                  </AddressListItemWrap>
                  <Divider
                    h={'1px'}
                    bgColor={theme.colors.neutral_colors.gray[100]}
                  />
                </React.Fragment>
              );
            })}
          </AddressList>
        </div>
      )}
      {totalNum !== 0 && totalNum <= MINIMUM_EXCEEDING_VALUE && (
        <div>
          <div>
            <Detail>
              <span>{`“`}</span>
              <ActiveText>{`${value}`}</ActiveText>
              <span>{`”`}</span>
              <span>{` 검색한 결과 총 `}</span>
              <ActiveText>{`${totalNum}`}</ActiveText>
              <span>{`건 입니다.`}</span>
            </Detail>
            <Divider h={'1px'} mt={'16px'} />
          </div>
          <AddressList totalNum={totalNum} ref={ref}>
            {addressList.map((address, idx) => {
              return (
                <React.Fragment key={`address-${address.addrRoad}-${idx}`}>
                  <AddressListItemWrap
                    onClick={() => handleSearchAddress(address)}
                  >
                    <AddressListItem>
                      <AddressWrap>
                        <AddressBox>
                          <Flag flagColor="mono" width="44px">
                            도로명
                          </Flag>
                          <Address>{address.addrRoad}</Address>
                        </AddressBox>
                        <AddressBox>
                          <Flag flagColor="mono" width="44px">
                            지번
                          </Flag>
                          <Address>{address.addrJibun}</Address>
                        </AddressBox>
                      </AddressWrap>
                    </AddressListItem>
                    <PostNum>{address.post5No}</PostNum>
                  </AddressListItemWrap>
                  <Divider
                    h={'1px'}
                    bgColor={theme.colors.neutral_colors.gray[100]}
                  />
                </React.Fragment>
              );
            })}
          </AddressList>
        </div>
      )}
      {totalNum === 0 && (
        <div>
          <Detail>
            <span>{`“`}</span>
            <ActiveText>{`${value}`}</ActiveText>
            <span>{`”`}</span>
            <span>{` 검색한 결과 총 `}</span>
            <ActiveText>{`${totalNum}`}</ActiveText>
            <span>{`건 입니다.`}</span>
          </Detail>
          <Divider h={'1px'} mt={'16px'} />
          <NoSearchData>
            <EmptyIcon />
            <NoSearchTitle>검색 결과가 없습니다.</NoSearchTitle>
            <NoSearchSubtitle>
              정확한 주소를 확인하시고 다시 한번 검색해 주세요.
            </NoSearchSubtitle>
          </NoSearchData>
        </div>
      )}
    </>
  );
}

const DetailInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Detail = styled.div`
  ${theme.typography.body2};
`;

const SubDetail = styled.div`
  ${theme.typography.body2};
  & :first-child {
    color: ${theme.colors.neutral_colors.gray[600]};
  }
  & :last-child {
    color: ${theme.colors.accent_colors.blue.default};
  }
`;

const ActiveText = styled.span`
  font-weight: 700;
`;

const AddressList = styled.div<{ totalNum: number }>`
  max-height: ${({ totalNum }) =>
    totalNum > MINIMUM_EXCEEDING_VALUE ? '337px' : '377px'};
  overflow-y: auto;
  &::-webkit-scrollbar {
    display: block;
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    display: block;
    background-color: ${theme.colors.neutral_colors.gray[500]};
    border-radius: 40px;
  }
`;

const AddressListItemWrap = styled.div`
  padding-right: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 78px;
  cursor: pointer;
`;

const AddressListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const AddressWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 356px;
  padding: 14px 0px;
`;

const AddressBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const Address = styled.div`
  min-width: 328px;
  width: 100%;
  ${theme.typography.body2}
  color: ${theme.colors.neutral_colors.primary};
`;

const PostNum = styled.div`
  ${theme.typography.body2}
  color: ${theme.colors.neutral_colors.primary};
`;

const NoSearchData = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 100px;
`;

const NoSearchTitle = styled.div`
  margin-top: 12px;
  ${theme.typography.body1}
`;

const NoSearchSubtitle = styled.div`
  margin-top: 8px;
  ${theme.typography.caption1};
  color: ${theme.colors.neutral_colors.gray[700]};
`;

export default SearchAddressResult;
